<template>
  <BaseModal :modalTitle="$t('W: Tilpas dashboard')">
    <VeeForm as="div" class="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-6 pb-2">
      <WidgetPickerPanel
        v-model="localWidgets"
        surface="dashboard"
        :excludedTypes="excludedWidgetTypes"
        isPermissionFiltered />
    </VeeForm>

    <template #buttons>
      <UiBaseButton
        :text="$t('W: Nulstil')"
        theme="neutral"
        @button-click="resetToDefaults" />
      <UiBaseButton
        :text="$t('action.save')"
        :isLoading="isSaving"
        @button-click="onSave" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useModalStore } from '@/stores/modalStore'
import { Form as VeeForm } from 'vee-validate'
import { mapActions } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { useToast } from '@/globals/typescript/utils/toast'
import {
  getWidgetCatalogEntry,
  getDefaultDashboardWidgets,
} from '@team-milva/lab-models/widget'
import type { IWidgetInstance, TWidgetType } from '@team-milva/lab-models/widget'
import WidgetPickerPanel, {
  type IEditableWidgetInstance,
} from '@/components/widgets/WidgetPickerPanel.vue'

// My Recent Activity is deferred — no renderer exists yet, so it's kept out of the
// picker entirely rather than letting a user add a widget that then renders nothing.
const EXCLUDED_WIDGET_TYPES: TWidgetType[] = ['my-recent-activity']

export default {
  name: 'DashboardCustomizeModal',

  components: { VeeForm, WidgetPickerPanel },

  emits: ['close'],

  data() {
    const userStore = useUserStore()
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      modalEntryID: useModalStore().top?.id,

      // Drop any widget whose type no longer exists in the catalog (e.g. removed from the
      // codebase since the user last saved) — otherwise it renders as a blank, label-less
      // row here that can only be cleared by manually finding and deleting it. Filtering it
      // out of localWidgets also means the next save naturally prunes it from Firestore.
      localWidgets: (userStore.currentUser?.dashboardConfig.widgets ?? [])
        .filter((widget) => !!getWidgetCatalogEntry(widget.type))
        .map((widget) => ({ ...widget, config: { ...widget.config } })) as IEditableWidgetInstance[],
      excludedWidgetTypes: EXCLUDED_WIDGET_TYPES,
      isSaving: false,
    }
  },

  methods: {
    ...mapActions(useUserStore, ['updateUserDocPromise']),

    resetToDefaults(): void {
      this.localWidgets = getDefaultDashboardWidgets().map((widget) => ({
        ...widget,
        config: { ...widget.config },
      })) as IEditableWidgetInstance[]
    },

    async onSave(): Promise<void> {
      const userStore = useUserStore()
      const currentUser = userStore.currentUser
      if (!currentUser) {
        return
      }

      this.isSaving = true
      try {
        await this.updateUserDocPromise(currentUser.id, {
          dashboardConfig: { widgets: this.localWidgets as unknown as IWidgetInstance[] },
        })
        useToast().success(this.$t('W: Dashboard opdateret'))
        this.$emit('close')
      } catch (error) {
        console.error(error)
        useToast().error(this.$t('W: Kunne ikke opdatere dashboard'))
      } finally {
        this.isSaving = false
      }
    },
  },

  created() {
    // The whole dashboard layout, seeded from the user's saved config and committed only by
    // onSave. Re-arranging by dragging is not typed work, but it takes many more than one
    // click to redo — which is where the sweep's Tier B line was drawn.
    this.unsavedGuard.register({
      snapshot: () => ({ localWidgets: this.localWidgets }),
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
